import { ZuruflexApiError, type PromotionInput, type PromotionPatch } from '@zuruflex/api-client';
import {
  PROMOTION_AUDIENCES,
  PROMOTION_DISCOUNT_TYPES,
  PROMOTION_FUNDERS,
  PROMOTION_TRIGGERS,
  type CurrencyCode,
  type Promotion,
  type PromotionAudience,
  type PromotionDiscountType,
  type PromotionFunder,
  type PromotionTrigger,
} from '@zuruflex/types';
import {
  Alert,
  Button,
  Combobox,
  DatePicker,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormField,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
  type ComboboxOption,
} from '@zuruflex/ui';
import { toMajorUnits, toMinorUnits } from '@zuruflex/utils';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../lib/api.js';
import { lastDay } from '../lib/promotions.js';

const ANY = 'any';

/** Offered before the branches load, and whenever they carry nothing else. */
const FALLBACK_CURRENCIES: CurrencyCode[] = ['GHS', 'RWF'];

interface Draft {
  name: string;
  description: string;
  trigger: PromotionTrigger;
  code: string;
  funder: PromotionFunder;
  partnerId: string;
  audience: PromotionAudience;
  discountType: PromotionDiscountType;
  /** Percent, as typed: "12.5". */
  percent: string;
  /** Major units, as typed. */
  amountOff: string;
  maxDiscount: string;
  currency: CurrencyCode;
  countryCode: string;
  startsAt: Date | undefined;
  /** The last day it runs, inclusive; the API's `endsAt` is the day after. */
  lastDay: Date | undefined;
  maxRedemptions: string;
  maxPerCustomer: string;
  minDays: string;
  minSubtotal: string;
  vehicleIds: string[];
  locationIds: string[];
  partnerIds: string[];
}

function majorOrEmpty(minor: number | null, currency: string): string {
  return minor === null ? '' : String(toMajorUnits(minor, currency));
}

function draftFrom(promo: Promotion | undefined): Draft {
  if (!promo) {
    return {
      name: '',
      description: '',
      trigger: 'CODE',
      code: '',
      funder: 'ZURUFLEX',
      partnerId: '',
      audience: 'EVERYONE',
      discountType: 'PERCENT',
      percent: '',
      amountOff: '',
      maxDiscount: '',
      currency: 'GHS',
      countryCode: ANY,
      startsAt: undefined,
      lastDay: undefined,
      maxRedemptions: '',
      maxPerCustomer: '1',
      minDays: '',
      minSubtotal: '',
      vehicleIds: [],
      locationIds: [],
      partnerIds: [],
    };
  }
  return {
    name: promo.name,
    description: promo.description ?? '',
    trigger: promo.trigger,
    code: promo.code ?? '',
    funder: promo.funder,
    partnerId: promo.partnerId ?? '',
    audience: promo.audience,
    discountType: promo.discountType,
    percent: promo.percentBps === null ? '' : String(promo.percentBps / 100),
    amountOff: majorOrEmpty(promo.amountOff, promo.currency),
    maxDiscount: majorOrEmpty(promo.maxDiscount, promo.currency),
    currency: promo.currency,
    countryCode: promo.countryCode ?? ANY,
    startsAt: new Date(promo.startsAt),
    lastDay: promo.endsAt ? lastDay(promo.endsAt) : undefined,
    maxRedemptions: promo.maxRedemptions === null ? '' : String(promo.maxRedemptions),
    maxPerCustomer: String(promo.maxPerCustomer),
    minDays: promo.minDays === null ? '' : String(promo.minDays),
    minSubtotal: majorOrEmpty(promo.minSubtotal, promo.currency),
    vehicleIds: promo.vehicleIds,
    locationIds: promo.locationIds,
    partnerIds: promo.partnerIds,
  };
}

/** Empty is "no limit"; anything else is a whole number. */
function intOrNull(raw: string): number | null {
  return raw.trim() === '' ? null : Math.round(Number(raw));
}

function minorOrNull(raw: string, currency: string): number | null {
  return raw.trim() === '' ? null : toMinorUnits(Number(raw), currency);
}

function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

/** The exclusive end: midnight at the start of the day after the last one. */
function endAfter(day: Date): Date {
  return new Date(day.getFullYear(), day.getMonth(), day.getDate() + 1);
}

function sameDay(a: Date | undefined, b: Date | undefined): boolean {
  return (a ? startOfDay(a).getTime() : null) === (b ? startOfDay(b).getTime() : null);
}

export interface PromotionDialogProps {
  /** Absent to create one. */
  promotion?: Promotion;
  onClose: () => void;
  /** Called with the saved promotion, so a create can go straight to it. */
  onSaved?: (promotion: Promotion) => void;
}

/**
 * Create a promotion, or change one.
 *
 * Who pays and how it triggers are fixed once it exists — the API refuses
 * to change them, because a booking already priced against it would then
 * have been funded by somebody else — so on an edit those two show as
 * settled facts rather than as controls.
 */
export function PromotionDialog({ promotion, onClose, onSaved }: PromotionDialogProps) {
  const { t } = useTranslation(['admin', 'common']);
  const queryClient = useQueryClient();
  const editing = promotion !== undefined;
  const [original] = useState(() => draftFrom(promotion));
  const [draft, setDraft] = useState<Draft>(original);
  const [problem, setProblem] = useState<string | null>(null);

  const set = <K extends keyof Draft>(key: K, value: Draft[K]) =>
    setDraft((current) => ({ ...current, [key]: value }));

  const partners = useQuery({
    queryKey: ['partners', 'all'],
    queryFn: () => api.listPartners({ perPage: 100 }),
  });
  const locations = useQuery({ queryKey: ['locations'], queryFn: () => api.listLocations() });

  // A partner-funded promo can only target that partner's own cars.
  const vehicleOwner = draft.funder === 'PARTNER' ? draft.partnerId : '';
  const vehicles = useQuery({
    queryKey: ['fleet', 'promotion-targets', vehicleOwner],
    queryFn: () =>
      api.listVehicles({ perPage: 100, ...(vehicleOwner ? { partnerId: vehicleOwner } : {}) }),
    enabled: draft.funder === 'ZURUFLEX' || Boolean(vehicleOwner),
  });

  const currencies = useMemo(() => {
    const seen = new Set<CurrencyCode>((locations.data ?? []).map((location) => location.currency));
    if (seen.size === 0) FALLBACK_CURRENCIES.forEach((code) => seen.add(code));
    seen.add(draft.currency);
    return [...seen].sort();
  }, [locations.data, draft.currency]);

  const countries = useMemo(
    () => [...new Set((locations.data ?? []).map((location) => location.country))].sort(),
    [locations.data],
  );

  const partnerOptions = useMemo<ComboboxOption[]>(
    () =>
      (partners.data?.items ?? []).map((partner) => ({
        value: partner.id,
        label: partner.displayName,
      })),
    [partners.data],
  );
  const locationOptions = useMemo<ComboboxOption[]>(
    () =>
      (locations.data ?? [])
        // A partner promo can use Zuruflex branches and that partner's own points.
        .filter(
          (location) =>
            draft.funder === 'ZURUFLEX' ||
            location.partnerId === null ||
            location.partnerId === draft.partnerId,
        )
        .map((location) => ({
          value: location.id,
          label: location.name,
          description: `${location.city}, ${location.country}`,
        })),
    [locations.data, draft.funder, draft.partnerId],
  );
  const vehicleOptions = useMemo<ComboboxOption[]>(
    () =>
      (vehicles.data?.items ?? []).map((vehicle) => ({
        value: vehicle.id,
        label: `${vehicle.make} ${vehicle.model} ${vehicle.year}`,
        description: vehicle.plateNumber,
      })),
    [vehicles.data],
  );

  function validate(): string | null {
    if (draft.name.trim().length < 2) return t('promotions.form.nameRequired');
    if (draft.trigger === 'CODE' && !draft.code.trim()) return t('promotions.form.codeRequired');
    const value = draft.discountType === 'PERCENT' ? draft.percent : draft.amountOff;
    if (!(Number(value) > 0)) return t('promotions.form.valueRequired');
    if (draft.discountType === 'PERCENT' && Number(value) > 100) {
      return t('promotions.form.percentTooHigh');
    }
    if (!editing && draft.funder === 'PARTNER' && !draft.partnerId) {
      return t('promotions.form.partnerRequired');
    }
    if (draft.startsAt && draft.lastDay && startOfDay(draft.lastDay) < startOfDay(draft.startsAt)) {
      return t('promotions.form.endBeforeStart');
    }
    return null;
  }

  /** Everything both a create and an edit may send. */
  function terms(): PromotionPatch {
    const { currency } = draft;
    return {
      name: draft.name.trim(),
      description: draft.description.trim() || null,
      ...(draft.trigger === 'CODE' ? { code: draft.code.trim().toUpperCase() } : {}),
      audience: draft.audience,
      discountType: draft.discountType,
      percentBps: draft.discountType === 'PERCENT' ? Math.round(Number(draft.percent) * 100) : null,
      amountOff: draft.discountType === 'FIXED' ? minorOrNull(draft.amountOff, currency) : null,
      maxDiscount:
        draft.discountType === 'PERCENT' ? minorOrNull(draft.maxDiscount, currency) : null,
      currency,
      countryCode: draft.countryCode === ANY ? null : draft.countryCode,
      maxRedemptions: intOrNull(draft.maxRedemptions),
      maxPerCustomer: intOrNull(draft.maxPerCustomer) ?? 1,
      minDays: intOrNull(draft.minDays),
      minSubtotal: minorOrNull(draft.minSubtotal, currency),
      vehicleIds: draft.vehicleIds,
      locationIds: draft.locationIds,
      ...(draft.funder === 'ZURUFLEX' ? { partnerIds: draft.partnerIds } : {}),
    };
  }

  const save = useMutation({
    mutationFn: async (): Promise<Promotion> => {
      const base = terms();
      if (promotion) {
        // Dates go only when they were changed: re-sending a start of
        // "midnight today" would quietly move a promo that began at noon.
        const patch: PromotionPatch = { ...base };
        if (!sameDay(draft.startsAt, original.startsAt) && draft.startsAt) {
          patch.startsAt = startOfDay(draft.startsAt).toISOString();
        }
        if (!sameDay(draft.lastDay, original.lastDay)) {
          patch.endsAt = draft.lastDay ? endAfter(draft.lastDay).toISOString() : null;
        }
        return api.updatePromotion(promotion.id, patch);
      }
      const input: PromotionInput = {
        ...base,
        name: base.name ?? draft.name.trim(),
        discountType: draft.discountType,
        currency: draft.currency,
        trigger: draft.trigger,
        funder: draft.funder,
        ...(draft.funder === 'PARTNER' ? { partnerId: draft.partnerId } : {}),
        ...(draft.startsAt && startOfDay(draft.startsAt) > startOfDay(new Date())
          ? { startsAt: startOfDay(draft.startsAt).toISOString() }
          : {}),
        ...(draft.lastDay ? { endsAt: endAfter(draft.lastDay).toISOString() } : {}),
      };
      return api.createPromotion(input);
    },
    onSuccess: (saved) => {
      void queryClient.invalidateQueries({ queryKey: ['promotions'] });
      void queryClient.invalidateQueries({ queryKey: ['promotion', saved.id] });
      onSaved?.(saved);
      onClose();
    },
  });

  function submit() {
    const invalid = validate();
    setProblem(invalid);
    if (!invalid) save.mutate();
  }

  const partnerName = (id: string) =>
    partners.data?.items.find((partner) => partner.id === id)?.displayName ?? '—';

  return (
    <Dialog open onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {editing
              ? t('promotions.form.editTitle', { name: promotion.name })
              : t('promotions.form.createTitle')}
          </DialogTitle>
          <DialogDescription>{t('promotions.form.body')}</DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          <Section title={t('promotions.form.offer')}>
            <FormField label={t('promotions.form.name')} required className="sm:col-span-2">
              {(field) => (
                <Input
                  {...field}
                  value={draft.name}
                  maxLength={120}
                  placeholder={t('promotions.form.namePlaceholder')}
                  onChange={(event) => set('name', event.target.value)}
                />
              )}
            </FormField>

            <FormField
              label={t('promotions.form.description')}
              hint={t('promotions.form.descriptionHint')}
              className="sm:col-span-2"
            >
              {(field) => (
                <Textarea
                  {...field}
                  rows={2}
                  maxLength={1000}
                  value={draft.description}
                  onChange={(event) => set('description', event.target.value)}
                />
              )}
            </FormField>

            <FormField label={t('promotions.form.trigger')} hint={t('promotions.form.triggerHint')}>
              {(field) =>
                editing ? (
                  <Fixed id={field.id}>{t(`promotions.trigger.${draft.trigger}`)}</Fixed>
                ) : (
                  <Select
                    value={draft.trigger}
                    onValueChange={(value) => set('trigger', value as PromotionTrigger)}
                  >
                    <SelectTrigger id={field.id}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PROMOTION_TRIGGERS.map((value) => (
                        <SelectItem key={value} value={value}>
                          {t(`promotions.trigger.${value}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )
              }
            </FormField>

            {draft.trigger === 'CODE' ? (
              <FormField
                label={t('promotions.form.code')}
                hint={t('promotions.form.codeHint')}
                required
              >
                {(field) => (
                  <Input
                    {...field}
                    className="font-mono uppercase"
                    maxLength={32}
                    value={draft.code}
                    placeholder="ZF20"
                    onChange={(event) => set('code', event.target.value.toUpperCase())}
                  />
                )}
              </FormField>
            ) : (
              <div />
            )}

            <FormField label={t('promotions.form.discountType')}>
              {(field) => (
                <Select
                  value={draft.discountType}
                  onValueChange={(value) => set('discountType', value as PromotionDiscountType)}
                >
                  <SelectTrigger id={field.id}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PROMOTION_DISCOUNT_TYPES.map((value) => (
                      <SelectItem key={value} value={value}>
                        {t(`promotions.discountType.${value}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </FormField>

            <FormField label={t('promotions.form.currency')}>
              {(field) => (
                <Select
                  value={draft.currency}
                  onValueChange={(value) => set('currency', value as CurrencyCode)}
                >
                  <SelectTrigger id={field.id}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {currencies.map((code) => (
                      <SelectItem key={code} value={code}>
                        {code}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </FormField>

            {draft.discountType === 'PERCENT' ? (
              <>
                <FormField label={t('promotions.form.percent')} required>
                  {(field) => (
                    <Suffixed suffix="%">
                      <Input
                        {...field}
                        type="number"
                        inputMode="decimal"
                        min={0.01}
                        max={100}
                        step={0.5}
                        value={draft.percent}
                        onChange={(event) => set('percent', event.target.value)}
                      />
                    </Suffixed>
                  )}
                </FormField>
                <FormField
                  label={t('promotions.form.maxDiscount')}
                  hint={t('promotions.form.maxDiscountHint')}
                >
                  {(field) => (
                    <Suffixed suffix={draft.currency}>
                      <Input
                        {...field}
                        type="number"
                        inputMode="decimal"
                        min={0}
                        value={draft.maxDiscount}
                        onChange={(event) => set('maxDiscount', event.target.value)}
                      />
                    </Suffixed>
                  )}
                </FormField>
              </>
            ) : (
              <FormField label={t('promotions.form.amountOff')} required>
                {(field) => (
                  <Suffixed suffix={draft.currency}>
                    <Input
                      {...field}
                      type="number"
                      inputMode="decimal"
                      min={0}
                      value={draft.amountOff}
                      onChange={(event) => set('amountOff', event.target.value)}
                    />
                  </Suffixed>
                )}
              </FormField>
            )}
          </Section>

          <Section title={t('promotions.form.who')}>
            <FormField label={t('promotions.form.funder')} hint={t('promotions.form.funderHint')}>
              {(field) =>
                editing ? (
                  <Fixed id={field.id}>
                    {draft.funder === 'PARTNER'
                      ? t('promotions.paidByPartner', { partner: partnerName(draft.partnerId) })
                      : t('promotions.paidByZuruflex')}
                  </Fixed>
                ) : (
                  <Select
                    value={draft.funder}
                    onValueChange={(value) =>
                      setDraft((current) => ({
                        ...current,
                        funder: value as PromotionFunder,
                        // Targets chosen for one payer do not carry to another.
                        vehicleIds: [],
                        locationIds: [],
                        partnerIds: [],
                      }))
                    }
                  >
                    <SelectTrigger id={field.id}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PROMOTION_FUNDERS.map((value) => (
                        <SelectItem key={value} value={value}>
                          {t(`promotions.funder.${value}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )
              }
            </FormField>

            {draft.funder === 'PARTNER' && !editing ? (
              <FormField label={t('promotions.form.partner')} required>
                {(field) => (
                  <Combobox
                    id={field.id}
                    options={partnerOptions}
                    value={draft.partnerId}
                    onChange={(value) =>
                      setDraft((current) => ({
                        ...current,
                        partnerId: value,
                        vehicleIds: [],
                        locationIds: [],
                      }))
                    }
                    placeholder={t('promotions.form.partnerPlaceholder')}
                    searchPlaceholder={t('promotions.form.search')}
                    emptyMessage={t('promotions.form.nothing')}
                  />
                )}
              </FormField>
            ) : (
              <div />
            )}

            <FormField
              label={t('promotions.form.audience')}
              hint={t(`promotions.audienceHint.${draft.audience}`)}
            >
              {(field) => (
                <Select
                  value={draft.audience}
                  onValueChange={(value) => set('audience', value as PromotionAudience)}
                >
                  <SelectTrigger id={field.id}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PROMOTION_AUDIENCES.map((value) => (
                      <SelectItem key={value} value={value}>
                        {t(`promotions.audience.${value}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </FormField>

            <FormField label={t('promotions.form.country')}>
              {(field) => (
                <Select
                  value={draft.countryCode}
                  onValueChange={(value) => set('countryCode', value)}
                >
                  <SelectTrigger id={field.id}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ANY}>{t('promotions.form.anyCountry')}</SelectItem>
                    {countries.map((code) => (
                      <SelectItem key={code} value={code}>
                        {code}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </FormField>

            <FormField
              label={t('promotions.form.startsAt')}
              hint={t('promotions.form.startsAtHint')}
            >
              {(field) => (
                <DatePicker
                  id={field.id}
                  value={draft.startsAt}
                  onChange={(date) => set('startsAt', date)}
                  placeholder={t('promotions.form.today')}
                />
              )}
            </FormField>

            <FormField label={t('promotions.form.lastDay')} hint={t('promotions.form.lastDayHint')}>
              {(field) => (
                <div className="flex items-center gap-2">
                  <DatePicker
                    id={field.id}
                    value={draft.lastDay}
                    onChange={(date) => set('lastDay', date)}
                    placeholder={t('promotions.form.noEnd')}
                  />
                  {draft.lastDay ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-sm"
                      aria-label={t('common:action.clear')}
                      onClick={() => set('lastDay', undefined)}
                    >
                      <X />
                    </Button>
                  ) : null}
                </div>
              )}
            </FormField>
          </Section>

          <Section title={t('promotions.form.limits')}>
            <FormField
              label={t('promotions.form.maxRedemptions')}
              hint={t('promotions.form.maxRedemptionsHint')}
            >
              {(field) => (
                <Input
                  {...field}
                  type="number"
                  min={1}
                  step={1}
                  value={draft.maxRedemptions}
                  onChange={(event) => set('maxRedemptions', event.target.value)}
                />
              )}
            </FormField>
            <FormField label={t('promotions.form.maxPerCustomer')}>
              {(field) => (
                <Input
                  {...field}
                  type="number"
                  min={1}
                  step={1}
                  value={draft.maxPerCustomer}
                  onChange={(event) => set('maxPerCustomer', event.target.value)}
                />
              )}
            </FormField>
            <FormField label={t('promotions.form.minDays')} hint={t('promotions.form.optional')}>
              {(field) => (
                <Input
                  {...field}
                  type="number"
                  min={1}
                  step={1}
                  value={draft.minDays}
                  onChange={(event) => set('minDays', event.target.value)}
                />
              )}
            </FormField>
            <FormField
              label={t('promotions.form.minSubtotal')}
              hint={t('promotions.form.minSubtotalHint')}
            >
              {(field) => (
                <Suffixed suffix={draft.currency}>
                  <Input
                    {...field}
                    type="number"
                    inputMode="decimal"
                    min={0}
                    value={draft.minSubtotal}
                    onChange={(event) => set('minSubtotal', event.target.value)}
                  />
                </Suffixed>
              )}
            </FormField>
          </Section>

          <Section title={t('promotions.form.where')}>
            <FormField
              label={t('promotions.form.vehicles')}
              hint={t('promotions.form.allWhenEmpty')}
              className="sm:col-span-2"
            >
              {(field) => (
                <MultiPick
                  id={field.id}
                  options={vehicleOptions}
                  value={draft.vehicleIds}
                  onChange={(ids) => set('vehicleIds', ids)}
                />
              )}
            </FormField>
            <FormField
              label={t('promotions.form.locations')}
              hint={t('promotions.form.allWhenEmpty')}
              className="sm:col-span-2"
            >
              {(field) => (
                <MultiPick
                  id={field.id}
                  options={locationOptions}
                  value={draft.locationIds}
                  onChange={(ids) => set('locationIds', ids)}
                />
              )}
            </FormField>
            {draft.funder === 'ZURUFLEX' ? (
              <FormField
                label={t('promotions.form.partners')}
                hint={t('promotions.form.partnersHint')}
                className="sm:col-span-2"
              >
                {(field) => (
                  <MultiPick
                    id={field.id}
                    options={partnerOptions}
                    value={draft.partnerIds}
                    onChange={(ids) => set('partnerIds', ids)}
                  />
                )}
              </FormField>
            ) : null}
          </Section>
        </div>

        {problem ? (
          <Alert variant="danger" className="mt-6">
            {problem}
          </Alert>
        ) : save.isError ? (
          <Alert variant="danger" className="mt-6">
            {save.error instanceof ZuruflexApiError ? save.error.message : t('common:error.unexpected')}
          </Alert>
        ) : null}

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            {t('common:action.cancel')}
          </Button>
          <Button isLoading={save.isPending} onClick={submit}>
            {editing ? t('promotions.form.save') : t('promotions.form.create')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="space-y-3">
      <h3 className="text-[11px] font-bold uppercase tracking-widest text-text-muted">{title}</h3>
      <div className="grid gap-4 sm:grid-cols-2">{children}</div>
    </section>
  );
}

/** A value that cannot change once the promotion exists, shown where its control would be. */
function Fixed({ id, children }: { id: string; children: ReactNode }) {
  return (
    <p
      id={id}
      className="flex h-10 items-center rounded-field bg-surface-sunken px-3.5 text-sm text-text"
    >
      {children}
    </p>
  );
}

function Suffixed({ suffix, children }: { suffix: string; children: ReactNode }) {
  return (
    <div className="flex items-center gap-2">
      {children}
      <span className="shrink-0 text-sm text-text-muted">{suffix}</span>
    </div>
  );
}

/**
 * Several picks from a long list: a search to add one, and the picks as
 * chips beneath it. Nothing picked means "all", which is what the API
 * reads an empty list as.
 */
function MultiPick({
  id,
  options,
  value,
  onChange,
}: {
  id: string;
  options: ComboboxOption[];
  value: string[];
  onChange: (ids: string[]) => void;
}) {
  const { t } = useTranslation(['admin', 'common']);
  const chosen = new Set(value);
  const labelOf = (key: string) => options.find((option) => option.value === key)?.label ?? key;

  return (
    <div className="space-y-2">
      <Combobox
        id={id}
        options={options.filter((option) => !chosen.has(option.value))}
        value=""
        onChange={(next) => onChange([...value, next])}
        placeholder={value.length === 0 ? t('promotions.form.all') : t('promotions.form.addMore')}
        searchPlaceholder={t('promotions.form.search')}
        emptyMessage={t('promotions.form.nothing')}
      />
      {value.length > 0 ? (
        <ul className="flex flex-wrap gap-1.5">
          {value.map((key) => (
            <li
              key={key}
              className="inline-flex items-center gap-1 rounded-pill bg-surface-sunken py-1 pl-3 pr-1 text-xs font-medium"
            >
              {labelOf(key)}
              <button
                type="button"
                onClick={() => onChange(value.filter((other) => other !== key))}
                aria-label={t('promotions.form.remove', { name: labelOf(key) })}
                className="inline-flex size-5 items-center justify-center rounded-full text-text-muted hover:bg-surface-hover hover:text-text"
              >
                <X className="size-3" />
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
